// Mapping a CSV onto lead fields, for Growth › Import leads. Pure, so it can be
// tested without a page: the page reads the file, this turns it into the rows
// POST /platform/growth/leads/import takes, and the server decides what happens
// to each one.

import type { LeadImportRow } from '@/api/types'
import type { Csv } from './csv'

export type LeadField = keyof LeadImportRow

/** Empty string in a mapping: leave the column out. */
export const IGNORE = ''
export type Mapping = Array<LeadField | typeof IGNORE>

export const LEAD_FIELDS: Array<{ value: LeadField; label: string; synonyms: string[] }> = [
  { value: 'name', label: 'Name', synonyms: ['name', 'league', 'league name', 'organisation', 'organization', 'org', 'team'] },
  { value: 'email', label: 'Email', synonyms: ['email', 'e-mail', 'contact email', 'email address'] },
  { value: 'website', label: 'Website', synonyms: ['website', 'site', 'url', 'web'] },
  { value: 'contact_name', label: 'Contact name', synonyms: ['contact', 'contact name', 'commissioner', 'director'] },
  { value: 'phone', label: 'Phone', synonyms: ['phone', 'contact phone', 'telephone', 'tel', 'mobile'] },
  { value: 'sport', label: 'Sport', synonyms: ['sport'] },
  { value: 'type', label: 'Type', synonyms: ['type', 'league type'] },
  { value: 'city', label: 'City', synonyms: ['city', 'town'] },
  { value: 'state', label: 'State', synonyms: ['state', 'province', 'region'] },
  { value: 'country', label: 'Country', synonyms: ['country'] },
  { value: 'platform', label: 'Current platform', synonyms: ['platform', 'current platform', 'software'] },
  { value: 'source', label: 'Source', synonyms: ['source'] },
  { value: 'source_url', label: 'Source URL', synonyms: ['source url', 'listing', 'listing url', 'profile url'] },
  { value: 'status', label: 'Status', synonyms: ['status', 'stage'] },
  { value: 'priority', label: 'Priority', synonyms: ['priority', 'score'] },
  { value: 'notes', label: 'Notes (add to)', synonyms: ['notes', 'note', 'comments'] },
]

export interface ImportDefaults {
  source: string
  platform: string
  sport: string
  type: string
  country: string
}

export const BLANK_DEFAULTS: ImportDefaults = { source: 'csv', platform: 'unknown', sport: '', type: 'other', country: 'US' }

const norm = (s: string) => s.trim().toLowerCase().replace(/[\s_-]+/g, ' ')

/** Each field takes the first column whose header is one of its names. Notes can take several. */
export function autoMap(headers: string[]): Mapping {
  const mapping: Mapping = headers.map(() => IGNORE)
  for (const field of LEAD_FIELDS) {
    const at = headers.findIndex((h, i) => mapping[i] === IGNORE && field.synonyms.some((s) => norm(s) === norm(h)))
    if (at >= 0) mapping[at] = field.value
  }
  return mapping
}

// The RecLeague list mined in July 2026 (recleague_email_list.csv). The columns
// with no field of their own go into notes, so nothing a person might want to
// read is dropped: the social links, the contact page, the team count and
// whether the league is archived. slug and date_mined are the mining run's own.
const RECLEAGUE_COLUMNS: Record<string, LeadField | typeof IGNORE> = {
  slug: IGNORE,
  league_name: 'name',
  sport: 'sport',
  teams: 'notes',
  archived: 'notes',
  recleague_url: 'source_url',
  contact_email: 'email',
  contact_phone: 'phone',
  contact_page: 'notes',
  website: 'website',
  facebook: 'notes',
  instagram: 'notes',
  other_social: 'notes',
  notes: 'notes',
  date_mined: IGNORE,
}

export function isRecLeagueList(headers: string[]): boolean {
  return headers.includes('recleague_url') && headers.includes('league_name')
}

/** The mapping and defaults a file starts from. */
export function suggestImport(headers: string[]): { mapping: Mapping; defaults: ImportDefaults; recLeague: boolean } {
  if (isRecLeagueList(headers)) {
    return {
      mapping: headers.map((h) => RECLEAGUE_COLUMNS[h] ?? IGNORE),
      defaults: { ...BLANK_DEFAULTS, source: 'recleague', platform: 'RecLeague', sport: 'basketball' },
      recLeague: true,
    }
  }
  return { mapping: autoMap(headers), defaults: BLANK_DEFAULTS, recLeague: false }
}

/** A column goes to one field; every field but notes takes one column, so choosing it clears the other. */
export function setMapping(mapping: Mapping, column: number, field: LeadField | typeof IGNORE): Mapping {
  return mapping.map((f, i) => {
    if (i === column) return field
    return field !== IGNORE && field !== 'notes' && f === field ? IGNORE : f
  })
}

export function buildRows(csv: Csv, mapping: Mapping, defaults: ImportDefaults): LeadImportRow[] {
  return csv.rows.map((cells) => {
    const row: LeadImportRow = {}
    const notes: string[] = []
    cells.forEach((raw, i) => {
      const field = mapping[i]
      const value = raw.trim()
      if (!field || !value) return
      if (field === 'notes') notes.push(norm(csv.headers[i]) === 'notes' ? value : `${csv.headers[i]}: ${value}`)
      else row[field] = value
    })
    if (notes.length) row.notes = notes.join('\n')
    for (const key of ['source', 'platform', 'sport', 'type', 'country'] as const) {
      const fallback = defaults[key].trim()
      if (!row[key] && fallback) row[key] = fallback
    }
    return row
  })
}
